// Redux slice for area selection. Owns the user's pick of areaType +
// areaRefId and the server-computed stats (sq mi, est population) plus the
// geometryToken that downstream calls (pricing, inquiry) re-use.
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../api/public-client.js';

export const fetchCounties = createAsyncThunk(
  'area/fetchCounties',
  async (_arg, { rejectWithValue }) => {
    try { return await apiFetch('/api/geography/counties'); } catch (err) { return rejectWithValue(err.message); }
  },
);

export const fetchPlaces = createAsyncThunk(
  'area/fetchPlaces',
  async (_arg, { rejectWithValue }) => {
    try { return await apiFetch('/api/geography/places'); } catch (err) { return rejectWithValue(err.message); }
  },
);

export const computeAreaStats = createAsyncThunk(
  'area/computeStats',
  async ({ areaType, areaRefId }, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/geometry/from-area', {
        method: 'POST',
        body: { areaType, areaRefId },
      });
    } catch (err) { return rejectWithValue(err.message); }
  },
);

export const uploadCustomGeometry = createAsyncThunk(
  'area/uploadCustomGeometry',
  async (file, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      return await apiFetch('/api/geometry/custom', { method: 'POST', body: formData });
    } catch (err) { return rejectWithValue(err.message || 'Upload failed'); }
  },
);

// Re-derives a fresh geometry token + stats from a saved custom boundary (used
// when resuming a draft) without requiring the user to re-upload the file.
export const restoreCustomGeometry = createAsyncThunk(
  'area/restoreCustomGeometry',
  async (geoJson, { rejectWithValue }) => {
    try {
      return await apiFetch('/api/geometry/custom-geojson', {
        method: 'POST',
        body: { geoJson },
      });
    } catch (err) { return rejectWithValue(err.message || 'Could not restore your saved area'); }
  },
);

const initialState = {
  areaType: 'county',
  areaRefId: null,
  counties: { data: null, loading: false, error: null },
  places: { data: null, loading: false, error: null },
  stats: {
    // { areaSqMi, estPopulation, name, geometryToken, and (m06) countyFips for
    // county selections / displayGeoJson for city + custom area highlights }
    data: null,
    loading: false,
    error: null,
  },
};

const areaSlice = createSlice({
  name: 'area',
  initialState,
  reducers: {
    setAreaType(state, action) {
      if (state.areaType !== action.payload) {
        state.areaType = action.payload;
        state.areaRefId = null;
        state.stats = { data: null, loading: false, error: null };
      }
    },
    setAreaRefId(state, action) {
      state.areaRefId = action.payload;
      if (action.payload == null) {
        state.stats = { data: null, loading: false, error: null };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCounties.pending, (s) => {
        s.counties.loading = true; s.counties.error = null;
      })
      .addCase(fetchCounties.fulfilled, (s, a) => {
        s.counties.loading = false; s.counties.data = a.payload;
      })
      .addCase(fetchCounties.rejected, (s, a) => {
        s.counties.loading = false;
        s.counties.error = a.payload || 'Failed to load counties';
      })
      .addCase(fetchPlaces.pending, (s) => {
        s.places.loading = true; s.places.error = null;
      })
      .addCase(fetchPlaces.fulfilled, (s, a) => {
        s.places.loading = false; s.places.data = a.payload;
      })
      .addCase(fetchPlaces.rejected, (s, a) => {
        s.places.loading = false;
        s.places.error = a.payload || 'Failed to load cities';
      })
      .addCase(computeAreaStats.pending, (s) => {
        s.stats.loading = true; s.stats.error = null;
      })
      .addCase(computeAreaStats.fulfilled, (s, a) => {
        s.stats.loading = false; s.stats.data = a.payload;
      })
      .addCase(computeAreaStats.rejected, (s, a) => {
        s.stats.loading = false;
        s.stats.error = a.payload || 'Failed to compute area stats';
      })
      .addCase(uploadCustomGeometry.pending, (s) => {
        s.stats.loading = true; s.stats.error = null;
      })
      .addCase(uploadCustomGeometry.fulfilled, (s, a) => {
        s.stats.loading = false; s.stats.data = a.payload;
      })
      .addCase(uploadCustomGeometry.rejected, (s, a) => {
        s.stats.loading = false;
        s.stats.error = a.payload || 'Failed to process the uploaded geometry';
      })
      .addCase(restoreCustomGeometry.pending, (s) => {
        s.stats.loading = true; s.stats.error = null;
      })
      .addCase(restoreCustomGeometry.fulfilled, (s, a) => {
        s.stats.loading = false; s.stats.data = a.payload;
      })
      .addCase(restoreCustomGeometry.rejected, (s, a) => {
        s.stats.loading = false;
        s.stats.error = a.payload || 'Could not restore your saved area';
      });
  },
});

export const { setAreaType, setAreaRefId } = areaSlice.actions;
export default areaSlice.reducer;
